import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { IconButton, Tooltip } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useRef } from 'react';
import './sentryFeedbackOverrides.css';

type SentryWithDialog = typeof Sentry & {
  showReportDialog?: (options?: Record<string, unknown>) => void;
};

type FeedbackForm = {
  appendToDom: () => void;
  removeFromDom: () => void;
  open: () => void;
};

export const BugReportButton = () => {
  const feedbackFormRef = useRef<FeedbackForm | null>(null);
  const isOpeningRef = useRef(false);

  const handleClick = async () => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;

    try {
      const feedback = Sentry.getFeedback();

      if (feedback) {
        // Use Sentry's own API to remove the previous instance — direct DOM
        // removal confuses Sentry's internal state and breaks subsequent opens.
        feedbackFormRef.current?.removeFromDom();
        feedbackFormRef.current = null;

        const form = (await feedback.createForm()) as FeedbackForm;
        form.appendToDom();

        feedbackFormRef.current = form;
        form.open();
        return;
      }

      const sentry = Sentry as SentryWithDialog;
      sentry.showReportDialog?.();
    } catch (error) {
      console.error('Failed to open feedback form', error);
    } finally {
      isOpeningRef.current = false;
    }
  };

  return (
    <Tooltip title="Report a bug">
      <IconButton color="inherit" aria-label="report a bug" onClick={handleClick}>
        <BugReportOutlinedIcon />
      </IconButton>
    </Tooltip>
  );
};
